import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { 
  ArrowLeft, Plus, Users, Mail, Phone, Calendar,
  Edit, Trash2, Key, Copy, Check, Loader2,
  Building, Shield, XCircle, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const KioskVolunteers = () => {
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();
  
  const [volunteers, setVolunteers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Create/Edit Modal
  const [showModal, setShowModal] = useState(false);
  const [editingVolunteer, setEditingVolunteer] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    campaign_ids: []
  });
  const [submitting, setSubmitting] = useState(false);
  
  // Password Modal
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [tempPassword, setTempPassword] = useState('');
  const [passwordCopied, setPasswordCopied] = useState(false);
  
  // Delete Modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingVolunteer, setDeletingVolunteer] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [volunteersRes, campaignsRes] = await Promise.all([
        api.get('/kiosk-volunteers'),
        api.get('/campaigns')
      ]);
      setVolunteers(volunteersRes.data);
      setCampaigns(campaignsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingVolunteer(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      campaign_ids: []
    });
    setShowModal(true);
  };

  const openEditModal = (volunteer) => {
    setEditingVolunteer(volunteer);
    setFormData({
      name: volunteer.name,
      email: volunteer.email,
      phone: volunteer.phone || '',
      campaign_ids: volunteer.campaign_ids || []
    });
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim() || !formData.email.trim()) {
      toast.error('Name and email are required');
      return;
    }
    if (formData.phone && formData.phone.replace(/\D/g, '').length < 7) {
      toast.error('Please enter a valid phone number');
      return;
    }
    
    setSubmitting(true);
    try {
      if (editingVolunteer) {
        // Update existing volunteer
        await api.put(`/kiosk-volunteers/${editingVolunteer.id}`, {
          name: formData.name,
          phone: formData.phone || null,
          campaign_ids: formData.campaign_ids
        });
        toast.success('Volunteer updated successfully');
      } else {
        // Create new volunteer
        const response = await api.post('/kiosk-volunteers', formData);
        setTempPassword(response.data.temporary_password);
        setShowPasswordModal(true);
        toast.success('Volunteer created successfully');
      }
      
      setShowModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingVolunteer) return;
    
    setDeleting(true);
    try {
      await api.delete(`/kiosk-volunteers/${deletingVolunteer.id}`);
      toast.success('Volunteer deleted successfully');
      setShowDeleteModal(false);
      setDeletingVolunteer(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete volunteer');
    } finally {
      setDeleting(false);
    }
  };

  const handleResetPassword = async (volunteer) => {
    try {
      const response = await api.post(`/kiosk-volunteers/${volunteer.id}/reset-password`);
      setTempPassword(response.data.temporary_password);
      setShowPasswordModal(true);
      toast.success('Password reset successfully');
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(tempPassword);
    setPasswordCopied(true);
    setTimeout(() => setPasswordCopied(false), 2000);
    toast.success('Password copied to clipboard');
  };

  const toggleCampaign = (campaignId) => {
    setFormData(prev => ({
      ...prev,
      campaign_ids: prev.campaign_ids.includes(campaignId)
        ? prev.campaign_ids.filter(id => id !== campaignId)
        : [...prev.campaign_ids, campaignId]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="hover:bg-slate-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-outfit font-bold text-slate-900">Kiosk Volunteers</h1>
            <p className="text-sm font-jakarta text-slate-600">
              Manage volunteers who can access Kiosk Mode for on-site registrations
            </p>
          </div>
        </div>
        
        <Button
          onClick={openCreateModal}
          className="bg-primary hover:bg-primary-hover"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Volunteer
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-gradient-to-r from-secondary/10 to-primary/10 border-0 p-4 mb-6">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-secondary mt-0.5" />
          <div>
            <p className="font-semibold text-slate-900 font-jakarta">Kiosk Volunteer Access</p>
            <p className="text-sm text-slate-600 font-jakarta">
              Kiosk volunteers can only access the Kiosk Mode for assigned events.
              They cannot access the admin dashboard or modify event settings.
            </p>
          </div>
        </div>
      </Card>

      {/* Volunteers List */}
      {volunteers.length === 0 ? (
        <Card className="bg-white rounded-xl border border-slate-100 p-12 text-center">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No Volunteers Yet</h3>
          <p className="text-slate-600 mb-4">
            Create kiosk volunteer accounts for your on-site registration desk staff.
          </p>
          <Button onClick={openCreateModal} className="bg-primary hover:bg-primary-hover">
            <Plus className="w-4 h-4 mr-2" />
            Add First Volunteer
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {volunteers.map((volunteer) => (
            <Card 
              key={volunteer.id} 
              className={`bg-white rounded-xl border shadow-card p-5 ${
                !volunteer.is_active ? 'opacity-60' : ''
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <span className="text-lg font-bold text-primary">
                      {volunteer.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 font-jakarta">{volunteer.name}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      volunteer.is_active 
                        ? 'bg-green-100 text-green-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {volunteer.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
                
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditModal(volunteer)}
                    className="text-slate-500 hover:text-primary"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleResetPassword(volunteer)}
                    className="text-slate-500 hover:text-amber-600"
                    title="Reset Password"
                  >
                    <Key className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setDeletingVolunteer(volunteer);
                      setShowDeleteModal(true);
                    }}
                    className="text-slate-500 hover:text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-slate-600">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <span className="truncate">{volunteer.email}</span>
                </div>
                {volunteer.phone && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <span>{volunteer.phone}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-slate-600">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  <span>Created {volunteer.created_at ? format(parseISO(volunteer.created_at), 'MMM dd, yyyy') : 'N/A'}</span>
                </div>
              </div>
              
              {/* Assigned Campaigns */}
              <div className="mt-4 pt-4 border-t border-slate-100">
                <p className="text-xs text-slate-500 font-jakarta mb-2 flex items-center gap-1">
                  <Building className="w-3 h-3" />
                  Assigned Events ({volunteer.campaign_names?.length || 0})
                </p>
                {volunteer.campaign_names?.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {volunteer.campaign_names.slice(0, 3).map((name, idx) => (
                      <span 
                        key={idx}
                        className="text-xs px-2 py-1 bg-slate-100 text-slate-700 rounded-full"
                      >
                        {name}
                      </span>
                    ))}
                    {volunteer.campaign_names.length > 3 && (
                      <span className="text-xs px-2 py-1 bg-slate-100 text-slate-500 rounded-full">
                        +{volunteer.campaign_names.length - 3} more
                      </span>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 italic">No events assigned</p>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-outfit">
              {editingVolunteer ? 'Edit Volunteer' : 'Add New Volunteer'}
            </DialogTitle>
            <DialogDescription>
              {editingVolunteer
                ? 'Update volunteer details and event assignments.'
                : 'Create a new kiosk volunteer account. A temporary password will be generated.'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-700 font-jakarta">Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter volunteer name"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label className="text-slate-700 font-jakarta">Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="volunteer@example.com"
                disabled={!!editingVolunteer}
                className="mt-1"
              />
              {editingVolunteer && (
                <p className="text-xs text-slate-500 mt-1">Email cannot be changed</p>
              )}
            </div>
            
            <div>
              <Label className="text-slate-700 font-jakarta">Phone</Label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/[^0-9]/g, '') })}
                placeholder="Enter phone number"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label className="text-slate-700 font-jakarta mb-2 block">
                Assign to Campaigns
              </Label>
              <div className="border border-slate-200 rounded-lg max-h-48 overflow-y-auto">
                {campaigns.length === 0 ? (
                  <p className="p-3 text-sm text-slate-500">No campaigns available</p>
                ) : (
                  campaigns.map((campaign) => (
                    <div
                      key={campaign.id}
                      onClick={() => toggleCampaign(campaign.id)}
                      className={`flex items-center justify-between p-3 cursor-pointer border-b border-slate-100 last:border-0 hover:bg-slate-50 ${
                        formData.campaign_ids.includes(campaign.id) ? 'bg-primary/5' : ''
                      }`}
                    >
                      <div>
                        <p className="font-medium text-slate-900 text-sm">{campaign.name}</p>
                        <p className="text-xs text-slate-500">{campaign.organization_name}</p>
                      </div>
                      {formData.campaign_ids.includes(campaign.id) ? (
                        <CheckCircle className="w-5 h-5 text-primary" />
                      ) : (
                        <div className="w-5 h-5 border-2 border-slate-300 rounded-full" />
                      )}
                    </div>
                  ))
                )}
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Selected: {formData.campaign_ids.length} campaign(s)
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-primary hover:bg-primary-hover"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : editingVolunteer ? (
                'Save Changes'
              ) : (
                'Create Volunteer'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Modal */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit">Temporary Password</DialogTitle>
            <DialogDescription>
              Share this password securely with the volunteer. They will need it to log in.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-slate-100 rounded-xl p-4 flex items-center justify-between">
              <span className="font-mono text-xl font-bold text-slate-900">{tempPassword}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={copyPassword}
                className="text-slate-600 hover:text-primary"
              >
                {passwordCopied ? (
                  <Check className="w-5 h-5 text-green-600" />
                ) : (
                  <Copy className="w-5 h-5" />
                )}
              </Button>
            </div>
            <p className="text-sm text-amber-600 mt-3 flex items-start gap-2">
              <Shield className="w-4 h-4 mt-0.5 flex-shrink-0" />
              This password will only be shown once. Make sure to copy it now.
            </p>
          </div>
          
          <DialogFooter>
            <Button 
              onClick={() => setShowPasswordModal(false)}
              className="bg-primary hover:bg-primary-hover w-full"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit text-red-600">Delete Volunteer</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this volunteer account? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {deletingVolunteer && (
            <div className="py-4">
              <div className="bg-red-50 rounded-xl p-4">
                <p className="font-semibold text-slate-900">{deletingVolunteer.name}</p>
                <p className="text-sm text-slate-600">{deletingVolunteer.email}</p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Delete Volunteer'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KioskVolunteers;
